/**
 * Free Gift Progress Bar + automatic gift add/remove
 * ------------------------------------------------------------------
 * Loaded sitewide via layout/theme.liquid (defer), right after theme.js.
 *
 * WHY THIS EXISTS AS ITS OWN SCRIPT
 * The gift needs to be added/removed automatically as a real cart line
 * item, based on eligibility, without depending on Impulse's internal
 * cart JS (which we don't have source access to). So this script owns
 * the whole loop itself: detect a cart change -> recompute eligibility
 * -> mutate the cart if needed -> refresh the visible drawer -> rebind
 * the controls on whatever it just rendered.
 *
 * SYNC MECHANISM
 * A MutationObserver on the stable #CartDrawer element (see init below)
 * detects any cart change, whether triggered by the customer through
 * Impulse's own UI or by this script's own mutations, and re-runs the
 * reconciler (syncCart).
 *
 * ELIGIBILITY
 * Eligible amount = cart.total_price MINUS the free gift product's own
 * line price (if it's currently in the cart). This keeps the gift from
 * ever contributing toward its own unlock threshold.
 *
 * GIFT ADD / REMOVE
 * On every reconcile pass:
 *   - eligible && gift missing      -> add 1 of the gift variant
 *   - eligible && gift qty != 1     -> correct quantity back to 1
 *   - not eligible && gift present  -> remove the gift
 * This covers automatic add, automatic remove, and "customer manually
 * removed the gift while still eligible" with the same single rule.
 *
 * REFRESHING THE VISIBLE DRAWER AFTER OUR OWN MUTATIONS
 * See refreshDrawerMarkup below: item list comes from cart.ajax.liquid
 * (the same alternate template Impulse itself fetches -- confirmed to
 * contain a real items loop, unlike the drawer's own markup which
 * renders that container empty on every normal page load). Subtotal
 * comes from /cart.js JSON, formatted the same way as the bar's own
 * money display. Quantity controls on the refreshed products are then
 * rebound (see bindQuantityControls), since Impulse only rebinds those
 * during its own refresh cycle, which this bypasses.
 */
(function () {
  var BAR_ID = 'GiftProgressBar';
  var syncing = false;
  var pendingRerun = false;
  var debounceTimer = null;

  function getBar() {
    return document.getElementById(BAR_ID);
  }

  function getThresholdCents() {
    var bar = getBar();
    if (!bar) return 0;
    return parseInt(bar.getAttribute('data-threshold-cents'), 10) || 0;
  }

  function getGiftVariantId() {
    var bar = getBar();
    if (!bar) return null;
    var id = parseInt(bar.getAttribute('data-gift-variant-id'), 10);
    return isNaN(id) || id <= 0 ? null : id;
  }

  function findGiftLine(cart, giftVariantId) {
    if (!giftVariantId || !cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === giftVariantId) return cart.items[i];
    }
    return null;
  }

  function formatMoney(cents) {
    var format =
      (window.theme && theme.settings && theme.settings.moneyFormat) || '\u20b9{{amount}}';

    function withDelimiters(number, precision, thousands, decimalSep) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimalSep = decimalSep === undefined ? '.' : decimalSep;
      if (isNaN(number)) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimalSep + parts[1] : '';
      return whole + fraction;
    }

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return format.replace(match[0], value);
  }

  function renderBar(eligibleCents) {
    var bar = getBar();
    if (!bar) return;

    var thresholdCents = getThresholdCents();
    if (thresholdCents <= 0) return;

    var messageBefore = bar.getAttribute('data-message-before') || '';
    var messageAfter = bar.getAttribute('data-message-after') || '';
    var messageEl = document.getElementById('GiftProgressBarMessage');
    var fillEl = document.getElementById('GiftProgressBarFill');
    if (!messageEl) return;

    if (eligibleCents >= thresholdCents) {
      bar.classList.add('is-complete');
      // innerHTML on purpose: Shopify's money format can legitimately
      // contain markup (e.g. <span class="money">...</span>).
      messageEl.innerHTML = messageAfter;
      if (fillEl) fillEl.style.width = '100%';
    } else {
      bar.classList.remove('is-complete');
      var remaining = thresholdCents - eligibleCents;
      messageEl.innerHTML = messageBefore.replace('[[remaining]]', formatMoney(remaining));
      var pct = Math.max(0, Math.min(100, (eligibleCents / thresholdCents) * 100));
      if (fillEl) fillEl.style.width = pct + '%';
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  /**
   * POST to a cart endpoint with automatic retry. fetch() only rejects
   * on a network failure, not on a non-2xx response -- so a Shopify-side
   * error (e.g. a transient failure) would previously be silently
   * ignored and the code would carry on as if the mutation had
   * succeeded. This retries a couple of times on a non-ok response
   * before giving up, so a real API hiccup can't look like "nothing
   * happened" (e.g. gift not actually removed even though we thought
   * we removed it).
   */
  function postCart(url, body, attempt) {
    attempt = attempt || 1;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok && attempt < 3) {
        return new Promise(function (resolve) {
          setTimeout(resolve, 300 * attempt);
        }).then(function () {
          return postCart(url, body, attempt + 1);
        });
      }
      return res;
    });
  }

  function addGift(variantId) {
    return postCart('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
  }

  function setGiftQuantity(variantId, quantity) {
    // /cart/change.js's "id" is the cart line's KEY, which Shopify
    // treats as a string (e.g. "40224568592481" for a line with no
    // custom properties). Sending it as a JSON number instead of a
    // string caused a 400 Bad Request on every single call -- which is
    // exactly why the gift was never actually being removed/corrected,
    // confirmed directly in the browser console. /cart/add.js's "id" is
    // genuinely the numeric variant ID, so that one is left untouched.
    return postCart('/cart/change.js', { id: String(variantId), quantity: quantity });
  }

  /**
   * Whenever WE replace [data-products] innerHTML ourselves (see
   * refreshDrawerMarkup below), the brand-new +/- buttons and quantity
   * inputs Impulse renders (snippets/cart-item.liquid: .js-qty__adjust,
   * .js-qty__num, .cart__remove a) have no click/change handlers on them
   * yet -- Impulse only (re)binds those during its OWN refresh cycle,
   * which our manual swap bypasses. Rather than guess at Impulse's
   * internal binding function, we bind these ourselves, directly, using
   * the real markup/attributes from cart-item.liquid. A guard flag
   * avoids double-binding if a node is ever reused instead of recreated.
   */
  function updateLineQuantity(lineKey, quantity) {
    if (!lineKey) return Promise.resolve();
    return postCart('/cart/change.js', { id: lineKey, quantity: quantity })
      .then(refreshDrawerMarkup)
      .then(scheduleSync);
  }

  function bindQuantityControls(container) {
    if (!container) return;

    container.querySelectorAll('.js-qty__num').forEach(function (input) {
      if (input.dataset.giftBarBound) return;
      input.dataset.giftBarBound = '1';
      input.addEventListener('change', function () {
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) qty = 0;
        updateLineQuantity(input.getAttribute('data-id'), qty);
      });
    });

    container.querySelectorAll('.js-qty__adjust--minus').forEach(function (btn) {
      if (btn.dataset.giftBarBound) return;
      btn.dataset.giftBarBound = '1';
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.js-qty__wrapper');
        var input = wrapper && wrapper.querySelector('.js-qty__num');
        if (!input) return;
        var qty = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
        updateLineQuantity(input.getAttribute('data-id'), qty);
      });
    });

    container.querySelectorAll('.js-qty__adjust--plus').forEach(function (btn) {
      if (btn.dataset.giftBarBound) return;
      btn.dataset.giftBarBound = '1';
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.js-qty__wrapper');
        var input = wrapper && wrapper.querySelector('.js-qty__num');
        if (!input) return;
        var qty = (parseInt(input.value, 10) || 0) + 1;
        updateLineQuantity(input.getAttribute('data-id'), qty);
      });
    });

    container.querySelectorAll('.cart__remove a').forEach(function (link) {
      if (link.dataset.giftBarBound) return;
      link.dataset.giftBarBound = '1';
      link.addEventListener('click', function (e) {
        e.preventDefault();
        try {
          var url = new URL(link.href, window.location.origin);
          updateLineQuantity(url.searchParams.get('id'), 0);
        } catch (err) {}
      });
    });
  }

  /**
   * Refresh the drawer after any cart mutation WE make.
   *
   * [data-products] in cart-drawer.liquid is rendered as a permanently
   * EMPTY div on every normal page load -- Impulse fills it in only via
   * its own client-side fetch of the cart.ajax.liquid alternate
   * template. A plain fetch of the current page (or any page) therefore
   * NEVER contains the rendered item list; that was the bug in the
   * previous version of this function (subtotal updated, product list
   * went blank). Fixed here by using the two sources that are actually
   * confirmed to contain the right data:
   *   - cart.ajax.liquid (via ?view=ajax) for the item list -- this is
   *     the same endpoint Impulse itself uses, and it does contain a
   *     real {% for item in cart.items %} loop.
   *   - /cart.js (already used elsewhere in this file) for total_price,
   *     formatted with the same formatMoney() used for the bar --
   *     no page fetch needed for this piece at all.
   * [data-discounts] is intentionally left alone for now (not part of
   * the reported bug); it may go stale after our own refresh on the
   * rare order using a cart-level discount, worth a follow-up if that
   * matters for this store.
   */
  function refreshDrawerMarkup() {
    var cartPageUrl = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';

    var itemsFetch = fetch(cartPageUrl + '?view=ajax', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.text(); });

    return Promise.all([itemsFetch, fetchCart()]).then(function (results) {
      var itemsHtml = results[0];
      var cart = results[1];

      var products = document.querySelector('#CartDrawer [data-products]');
      if (products) {
        products.innerHTML = itemsHtml;
        bindQuantityControls(products);
      }

      var subtotalEl = document.querySelector('#CartDrawer [data-subtotal]');
      if (subtotalEl) {
        subtotalEl.innerHTML = formatMoney(cart.total_price);
      }
    });
  }

  function syncCart() {
    var giftVariantId = getGiftVariantId();
    var thresholdCents = getThresholdCents();

    return fetchCart().then(function (cart) {
      var giftLine = findGiftLine(cart, giftVariantId);
      var giftLinePrice = giftLine ? giftLine.line_price : 0;
      var eligibleCents = cart.total_price - giftLinePrice;
      var isEligible = thresholdCents > 0 && eligibleCents >= thresholdCents;

      if (!giftVariantId) {
        renderBar(eligibleCents);
        return null;
      }

      if (isEligible && !giftLine) {
        return addGift(giftVariantId)
          .then(refreshDrawerMarkup)
          .then(function () {
            renderBar(eligibleCents);
          });
      }

      if (isEligible && giftLine && giftLine.quantity !== 1) {
        return setGiftQuantity(giftVariantId, 1)
          .then(refreshDrawerMarkup)
          .then(function () {
            renderBar(eligibleCents);
          });
      }

      if (!isEligible && giftLine) {
        return setGiftQuantity(giftVariantId, 0)
          .then(refreshDrawerMarkup)
          .then(function () {
            renderBar(eligibleCents);
          });
      }

      renderBar(eligibleCents);
      return null;
    });
  }

  function scheduleSync() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      if (syncing) {
        pendingRerun = true;
        return;
      }
      syncing = true;
      syncCart()
        .catch(function () {})
        .then(function () {
          syncing = false;
          if (pendingRerun) {
            pendingRerun = false;
            scheduleSync();
          }
        });
    }, 150);
  }

  function init() {
    // Watch the stable outer drawer element rather than the inner
    // [data-products] node directly. If that inner node ever gets
    // replaced wholesale (not just its contents), an observer attached
    // to it goes silently dead; #CartDrawer itself is rendered once per
    // page load and is far less likely to ever be swapped out.
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    var observer = new MutationObserver(scheduleSync);
    observer.observe(drawer, { childList: true, subtree: true });

    // Belt-and-suspenders: self-heal on a timer too, in case some cart
    // change happens without producing an observable mutation here
    // (e.g. another app's script touching the cart in a way that
    // doesn't route through this drawer at all).
    setInterval(scheduleSync, 4000);

    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Small public hook so other independent scripts on the page (e.g.
  // assets/rakhi-addon.js) can trigger the same reliable drawer sync
  // after their own cart mutations, instead of re-implementing this
  // logic themselves. Only scheduleSync is exposed -- everything else
  // in this closure stays private.
  window.WriteHouseCartSync = window.WriteHouseCartSync || {};
  window.WriteHouseCartSync.refresh = scheduleSync;
})();
